import { useContext, useRef } from 'react'
import { ChevronUp, ChevronDown, X, Plus, GripVertical } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { columnsAfterDrag } from './columnsReorder'

/**
 * Generic "choose + reorder columns" control, bound to a store array of
 * active column keys (their ORDER in the array IS the display order).
 *
 * Owns the whole interaction in JS for the same reason `FacetList` does:
 * reactive-json's `Switch` can't iterate an array of bare scalars (see the
 * project's own gotcha on this), and move-up/move-down/add/remove/drag on an
 * ordered array has no YAML primitive (`toggleData` only appends/removes, it
 * never reorders). Pairs with `OrderedColumns`, which renders the table
 * itself in this same array's order.
 *
 * Reordering is offered TWO ways — a drag handle (`@dnd-kit`, already a
 * project dependency via `SortableList`) for pointer/touch users, and the
 * ▲▼ buttons for everyone else (keyboard, screen readers, or anyone who
 * just prefers clicking). Both write the same store array through the same
 * path, so either one alone is enough to reorder — nothing depends on drag
 * actually working.
 *
 * The whole control is the immediate child of `<ActionDependant>` — same
 * pattern as `FacetList`/`SortableList` — so the move/remove/add buttons'
 * native `click` bubbles up and runs the YAML `actions:` declared on this
 * element (typically the page's usual persist chain ; no refetch is needed,
 * reordering columns changes no query parameter).
 *
 * DRAG uses a DIFFERENT event on purpose : `@dnd-kit` installs its own
 * capture-phase `click` swallower on `document` for ~50 ms after every drag
 * (its defence against the browser's ghost-click on whatever the pointer
 * released over) — a `click` dispatched from drag-end, synthetic or not,
 * never reaches React's handler in that window, `@dnd-kit` stops it first
 * (confirmed against its source, not guessed). So drag-end fires a
 * `columnsreorder` CustomEvent on the container instead, entirely outside
 * `@dnd-kit`'s click machinery ; the page's YAML lists it as a second `on:`
 * alongside `click` wherever it declares the persist action.
 *
 * YAML usage :
 *   - type: ColumnsPicker
 *     dataLocation: ~~.columns        # ordered array of active column keys
 *     catalog: ~~.columnCatalog       # [{ key, label }, …] every choosable column
 *     actions:
 *       - { what: submitData, on: click, url: ~~.savedViewUrl, ... }          # buttons
 *       - { what: submitData, on: columnsreorder, url: ~~.savedViewUrl, ... } # drag
 */
const DragRow = ({ id, label, onMoveUp, onMoveDown, onRemove, disableUp, disableDown, rowClass, iconBtnClass }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <div ref={setNodeRef} style={style} className={rowClass}>
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Glisser pour déplacer la colonne « ${label} »`}
      >
        <GripVertical size={14} aria-hidden="true" />
      </button>
      <span className="flex-1 truncate">{label}</span>
      <button
        type="button"
        className={iconBtnClass}
        disabled={disableUp}
        aria-label={`Monter la colonne « ${label} »`}
        onClick={onMoveUp}
      >
        <ChevronUp size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={iconBtnClass}
        disabled={disableDown}
        aria-label={`Descendre la colonne « ${label} »`}
        onClick={onMoveDown}
      >
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={iconBtnClass}
        aria-label={`Retirer la colonne « ${label} »`}
        onClick={onRemove}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}

export const ColumnsPicker = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const catalog = resolve(props.catalog)
  const entries = Array.isArray(catalog) ? catalog : []
  // A key the catalog no longer defines (a saved view remembering a column
  // the page has since dropped) is neither listed nor kept : `OrderedColumns`
  // already skips it, and the next write here drops it from the store.
  const stored = Array.isArray(formData) ? formData : []
  const active = entries.length ? stored.filter((k) => entries.some((e) => e?.key === k)) : stored
  const ids = active.map((_, i) => `col-${i}`)
  const labelFor = (key) => entries.find((e) => e?.key === key)?.label ?? key
  const inactive = entries.filter((e) => e?.key && !active.includes(e.key))

  const write = (next) => globalDataContext.updateData(next, formDataPath)
  const moveUp = (i) => {
    if (i <= 0) return
    const next = [...active]
    ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
    write(next)
  }
  const moveDown = (i) => {
    if (i >= active.length - 1) return
    const next = [...active]
    ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
    write(next)
  }
  const remove = (key) => write(active.filter((k) => k !== key))
  const add = (key) => write([...active, key])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const onDragEnd = ({ active: dragged, over }) => {
    const next = columnsAfterDrag(active, dragged?.id, over?.id)
    if (!next) return
    write(next)
    // NOT a "click": @dnd-kit installs a capture-phase `click` swallower on
    // `document` for ~50ms after every drag (its own defence against the
    // browser's ghost-click on whatever the pointer released over) — a
    // synthetic click dispatched in that window never reaches React's
    // handler, dnd-kit stops it first. A distinct event name sidesteps that
    // fight entirely ; the page's YAML lists it as a second `on:` next to
    // `click` on the same actions.
    containerRef.current?.dispatchEvent(new CustomEvent('columnsreorder', { bubbles: true }))
  }

  const rowClass = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors'
  const iconBtnClass = 'shrink-0 p-1 rounded text-slate-400 hover:text-iris-600 hover:bg-iris-50 disabled:opacity-30 disabled:pointer-events-none transition-colors'

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div role="group" aria-label="Colonnes affichées, dans leur ordre">
              {active.map((key, i) => (
                <DragRow
                  key={ids[i]}
                  id={ids[i]}
                  label={labelFor(key)}
                  onMoveUp={() => moveUp(i)}
                  onMoveDown={() => moveDown(i)}
                  onRemove={() => remove(key)}
                  disableUp={i === 0}
                  disableDown={i === active.length - 1}
                  rowClass={rowClass}
                  iconBtnClass={iconBtnClass}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {inactive.length > 0 && (
          <div role="group" aria-label="Ajouter une colonne" className="mt-1 pt-1 border-t border-slate-100">
            {inactive.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={`flex w-full items-center gap-1.5 text-left ${rowClass}`}
                onClick={() => add(entry.key)}
              >
                <Plus size={13} className="shrink-0 text-slate-400" aria-hidden="true" />
                <span className="truncate">{entry.label ?? entry.key}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
